import { useState, useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import {
  Home,
  Clock,
  Combine,
  Scissors,
  Droplets,
  Image,
  FileDigit,
  FileText,
  Shrink,
  Crop,
  FileMinus2,
  ListOrdered,
  Search,
  Lock,
  Unlock,
  Pencil,
  ChevronRight,
  ChevronDown,
  CheckCircle2,
  XCircle,
  Shield,
  Code,
  PenTool,
  FolderArchive,
  ScanText,
  Users,
  Stamp,
  FileOutput,
  Brain,
  Sparkles,
  Settings,
  MessageSquare,
  Layout,
  Maximize,
  Layers,
  Layers3,
  ClipboardList,
  Calendar,
  FileTextIcon,
  Target,
  BookOpen,
  RotateCw,
  Hash,
  EyeOff,
  ImageIcon,
  Link as LinkIcon,
  Wifi,
  PenTool as PenToolIcon,
  Presentation,
  GitCompareArrows,
  UserCheck,
  GraduationCap,
  Scale,
  FileDiff,
  DownloadCloud,
} from "lucide-react";
import { NavLink } from "@/components/NavLink";
import { useToolHistory } from "@/context/ToolHistoryContext";
import { Sidebar, SidebarContent } from "@/components/ui/sidebar";
import pdfWorkspaceLogo from "@/assets/logo.png";

// Define tool categories with their tools
const toolCategories = [
  {
    name: "Edit",
    icon: Pencil,
    tools: [
      { title: "Edit PDF", url: "/tools/edit-pdf", icon: Pencil, pro: true },
      {
        title: "Link Manager",
        url: "/tools/link-manager",
        icon: LinkIcon,
        pro: true,
      },
      { title: "eSign PDF", url: "/tools/esign-pdf", icon: PenTool, pro: true },
      { title: "Crop PDF", url: "/tools/crop", icon: Crop },
      { title: "Remove Pages", url: "/tools/remove-pages", icon: FileMinus2 },
      {
        title: "Add Page Numbers",
        url: "/tools/page-numbers",
        icon: ListOrdered,
      },
      { title: "Watermark", url: "/tools/watermark", icon: Droplets },
      {
        title: "Redact Text",
        url: "/tools/redact-text",
        icon: Shield,
        pro: true,
      },
      {
        title: "Edit Layout",
        url: "/tools/edit-pdf-layout",
        icon: Layout,
      },
      {
        title: "Header & Footer",
        url: "/tools/header-footer",
        icon: FileText,
      },
      {
        title: "Flatten PDF",
        url: "/tools/flatten-pdf",
        icon: Layers,
      },
    ],
  },
  {
    name: "Convert",
    icon: FileOutput,
    tools: [
      { title: "PDF → Image", url: "/tools/convert/pdf-to-image", icon: Image },
      {
        title: "PDF → Word",
        url: "/tools/convert/pdf-to-word",
        icon: FileDigit,
      },
      { title: "PDF → HTML", url: "/tools/convert/pdf-to-html", icon: Code },
      {
        title: "PDF → ZIP",
        url: "/tools/pdf-to-zip",
        icon: FolderArchive,
        pro: true,
      },
      { title: "Image → PDF", url: "/tools/convert/image-to-pdf", icon: Image },
      { title: "Word → PDF", url: "/tools/word-to-pdf", icon: FileDigit },
      { title: "MD → PDF", url: "/tools/markdown-to-pdf", icon: FileText },
    ],
  },
  {
    name: "Organize",
    icon: Combine,
    tools: [
      { title: "Merge PDF", url: "/tools/merge", icon: Combine },
      { title: "Split PDF", url: "/tools/split", icon: Scissors },
      { title: "Mail Merge", url: "/tools/mail-merge", icon: Users },
    ],
  },
  {
    name: "Optimize",
    icon: Shrink,
    tools: [
      { title: "Compress PDF Size", url: "/tools/compress", icon: Shrink },
      {
        title: "Increase PDF Size",
        url: "/tools/increase-pdf-size",
        icon: Maximize,
      },
      {
        title: "Enhance PDF",
        url: "/tools/enhance-pdf",
        icon: Sparkles,
      },
    ],
  },
  {
    name: "Security",
    icon: Lock,
    tools: [
      { title: "Encrypt PDF", url: "/tools/encrypt", icon: Lock },
      { title: "Decrypt PDF", url: "/tools/decrypt", icon: Unlock },
      {
        title: "Deep Metadata",
        url: "/tools/deep-metadata-explorer",
        icon: Search,
      },
    ],
  },
  {
    name: "Extract",
    icon: ScanText,
    tools: [
      {
        title: "Extract Form Data",
        url: "/tools/extract-form-data",
        icon: FileDigit,
      },
      { title: "OCR PDF", url: "/tools/ocr-pdf", icon: ScanText },
      { title: "PDF Inspector", url: "/tools/inspector", icon: Search },
    ],
  },
  {
    name: "AI Tools",
    icon: Brain,
    tools: [
      {
        title: "Chat with PDF",
        url: "/tools/ai/chat",
        icon: MessageSquare,
        pro: true,
      },
      {
        title: "Quiz Generator",
        url: "/tools/ai/quiz",
        icon: ClipboardList,
        pro: true,
      },
      {
        title: "Timeline",
        url: "/tools/ai/timeline",
        icon: Calendar,
        pro: true,
      },
      {
        title: "Summarize",
        url: "/tools/ai/summarize",
        icon: FileTextIcon,
        pro: true,
      },
      {
        title: "Key Points",
        url: "/tools/ai/keypoints",
        icon: Target,
        pro: true,
      },
      {
        title: "Flashcards",
        url: "/tools/ai/flashcards",
        icon: Layers,
        pro: true,
      },
      {
        title: "Study Guide",
        url: "/tools/ai/studyguide",
        icon: BookOpen,
        pro: true,
      },
    ],
  },
  {
    name: "Batch Tools",
    icon: Layers3,
    tools: [
      {
        title: "Batch Stamp",
        url: "/batch-tools/stamp",
        icon: Stamp,
        pro: true,
      },
      {
        title: "Bates Numbering",
        url: "/batch-tools/bates-numbering",
        icon: Hash,
        pro: true,
      },
      {
        title: "Dynamic Stamp",
        url: "/batch-tools/dynamic-stamp",
        icon: Stamp,
      },
      { title: "Batch Compress", url: "/batch-tools/compress", icon: Shrink },
      { title: "Batch Split", url: "/batch-tools/split", icon: Scissors },
      { title: "Batch Rotate", url: "/batch-tools/rotate", icon: RotateCw },
      { title: "Batch Layout", url: "/batch-tools/layout", icon: Layout },
      { title: "Batch Flatten", url: "/batch-tools/flatten", icon: Layers },
      { title: "Batch Encrypt", url: "/batch-tools/encrypt", icon: Lock },
      { title: "Batch Decrypt", url: "/batch-tools/decrypt", icon: Unlock },
      {
        title: "Page Numbers",
        url: "/batch-tools/page-numbers",
        icon: ListOrdered,
      },
      {
        title: "Extract Text",
        url: "/batch-tools/extract-text",
        icon: FileText,
      },
      {
        title: "PDF to Image",
        url: "/batch-tools/pdf-to-image",
        icon: ImageIcon,
      },
      { title: "Batch Redact", url: "/batch-tools/redact", icon: EyeOff },
      { title: "Clean Metadata", url: "/batch-tools/metadata", icon: Shield },
      {
        title: "Link Manager",
        url: "/batch-tools/link-manager",
        icon: LinkIcon,
        pro: true,
      },
      {
        title: "Batch OCR",
        url: "/batch-tools/ocr",
        icon: ScanText,
        pro: true,
      },
      {
        title: "Certificate Gen",
        url: "/batch-tools/certificate-generator",
        icon: FileTextIcon,
        pro: true,
      },
      {
        title: "Metadata Explorer",
        url: "/batch-tools/deep-metadata-explorer",
        icon: Shield,
      },
      {
        title: "Rule Engine",
        url: "/batch-tools/pdf-rule-engine",
        icon: Settings,
        pro: true,
      },
    ],
  },
  {
    name: "P2P Tools",
    icon: Wifi,
    tools: [
      {
        title: "Co-Annotation",
        url: "/p2p-tools/co-annotate",
        icon: PenToolIcon,
        pro: true,
      },
      {
        title: "Review & Approval",
        url: "/p2p-tools/review-approve",
        icon: ClipboardList,
        pro: true,
      },
      {
        title: "Form Fill & Sign",
        url: "/p2p-tools/form-fill-sign",
        icon: FileTextIcon,
        pro: true,
      },
      {
        title: "Real-time Collab",
        url: "/p2p-tools/realtime-collab",
        icon: Users,
        pro: true,
      },
      {
        title: "Presentation",
        url: "/p2p-tools/presentation",
        icon: Presentation,
        pro: true,
      },
      {
        title: "Redline Changes",
        url: "/p2p-tools/redline",
        icon: GitCompareArrows,
        pro: true,
      },
      {
        title: "Whiteboard",
        url: "/p2p-tools/whiteboard",
        icon: PenToolIcon,
        pro: true,
      },
      {
        title: "Multi-Signer",
        url: "/p2p-tools/multi-signer",
        icon: UserCheck,
        pro: true,
      },
      {
        title: "Classroom",
        url: "/p2p-tools/classroom",
        icon: GraduationCap,
        pro: true,
      },
      {
        title: "Contract Negotiation",
        url: "/p2p-tools/contract-negotiation",
        icon: Scale,
        pro: true,
      },
      {
        title: "Doc Comparison",
        url: "/p2p-tools/doc-comparison",
        icon: FileDiff,
        pro: true,
      },
    ],
  },
];

export const AppSidebar = () => {
  const location = useLocation();
  const { entries, clear } = useToolHistory();
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);

  useEffect(() => {
    const handler = (e: any) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setDeferredPrompt(null);
    }
  };

  const [expandedCategories, setExpandedCategories] = useState<
    Record<string, boolean>
  >({
    Edit: true,
    Convert: false,
    Organize: false,
    Optimize: false,
    Security: false,
    Extract: false,
    "AI Tools": false,
    "Batch Tools": false,
    "P2P Tools": false,
  });

  const currentPath = location.pathname;
  const isActive = (path: string) => currentPath === path;

  const toggleCategory = (name: string) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [name]: !prev[name],
    }));
  };

  const recent = entries.slice(0, 5);

  return (
    <Sidebar className="border-r border-border bg-background text-foreground sm:w-56 md:w-64">
      <SidebarContent className="flex flex-col h-full">
        {/* Logo */}
        <div className="flex items-center gap-3 border-b border-border px-4 py-4">
          <Link to="/" className="flex items-center gap-3 group">
            <img
              src={pdfWorkspaceLogo}
              alt="PDFWorkSpace Logo"
              className="h-8 w-8 transition-transform group-hover:scale-105"
            />
            <span className="font-serifAccent text-lg font-semibold tracking-tight">
              PDFWorkSpace
            </span>
          </Link>
        </div>

        {/* Home Link */}
        <div className="px-3 py-2">
          <NavLink
            to="/"
            className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all ${
              isActive("/")
                ? "bg-foreground text-background"
                : "text-text-secondary hover:bg-secondary hover:text-foreground"
            }`}
          >
            <Home className="h-4 w-4" />
            <span>Home</span>
          </NavLink>
        </div>

        {/* Tool Categories */}
        <div className="flex-1 overflow-y-auto px-3 py-2">
          <div className="space-y-1">
            {toolCategories.map((category) => (
              <div key={category.name}>
                {/* Category Header */}
                <button
                  type="button"
                  onClick={() => toggleCategory(category.name)}
                  className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold uppercase tracking-wider text-text-secondary hover:text-foreground transition-colors rounded-lg hover:bg-secondary/50"
                >
                  <div className="flex items-center gap-2">
                    <category.icon className="h-3.5 w-3.5" />
                    <span>{category.name}</span>
                  </div>
                  {expandedCategories[category.name] ? (
                    <ChevronDown className="h-3.5 w-3.5" />
                  ) : (
                    <ChevronRight className="h-3.5 w-3.5" />
                  )}
                </button>

                {/* Category Tools */}
                {expandedCategories[category.name] && (
                  <div className="ml-2 mt-1 space-y-0.5 border-l border-border/50 pl-3">
                    {category.tools.map((tool) => (
                      <NavLink
                        key={tool.url}
                        to={tool.url}
                        className={`flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm transition-all ${
                          isActive(tool.url)
                            ? "bg-foreground text-background font-medium"
                            : "text-text-secondary hover:bg-secondary hover:text-foreground"
                        }`}
                      >
                        <tool.icon className="h-4 w-4 shrink-0" />
                        <span className="truncate flex-1">{tool.title}</span>
                        {tool.pro && (
                          <span className="relative px-1.5 py-0.5 text-[0.55rem] font-extrabold uppercase tracking-widest bg-foreground text-background rounded border border-foreground/20 shadow-sm overflow-hidden group-hover:shadow-md transition-all">
                            <span className="relative z-10">PRO</span>
                            <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/50 to-transparent -translate-x-full animate-[shimmer_2s_infinite]" />
                          </span>
                        )}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Install App Button */}
        {deferredPrompt && (
          <div className="px-3 pb-3">
            <button
              onClick={handleInstallClick}
              className="w-full flex items-center justify-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-secondary hover:text-primary shadow-sm"
            >
              <DownloadCloud className="h-4 w-4" />
              Install Desktop App
            </button>
          </div>
        )}

        {/* Recent Activity */}
        {recent.length > 0 && (
          <div className="border-t border-border px-3 py-3">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-text-secondary">
                <Clock className="h-3.5 w-3.5" />
                Recent
              </div>
              <button
                type="button"
                onClick={clear}
                className="text-xs text-text-secondary hover:text-red-500 transition-colors"
              >
                Clear
              </button>
            </div>
            <div className="space-y-1">
              {recent.map((entry) => (
                <div
                  key={entry.id}
                  className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-xs text-text-secondary"
                >
                  {entry.status === "success" ? (
                    <CheckCircle2 className="h-3.5 w-3.5 text-green-500 shrink-0" />
                  ) : (
                    <XCircle className="h-3.5 w-3.5 text-red-500 shrink-0" />
                  )}
                  <span className="truncate">{entry.toolName}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </SidebarContent>
    </Sidebar>
  );
};
